import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import get from "lodash/get";
import includes from "lodash/includes";
import map from "lodash/map";
import { commencementDateTypes } from "../../../app/js/types/commencementDateTypes";
import { propertyManagerTypes } from "../../../app/js/types/propertyManagerTypes";
import Autocomplete from "../../../common/js/components/Autocomplete";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import { Add, Search } from "../../../common/js/icons";

const DefTerm = styled("span")({
    fontSize: "13px",
    textTransform: "uppercase",
    color: "#9E9E9E",
    fontWeight: 600,
    display: "inline-block",
    padding: "0 10px 3px",
});

const ManagementInfoContainer = styled("div")({
    marginBottom: "20px",
});

const ManagementInfoContainerHeader = styled("div")({
    padding: "10px 12px",
    marginTop: "15px",
    border: "1px solid #dbdbdb",
});

const ManagementInfoContainerRow = styled("div")({
    padding: "11px 12px",
    border: "1px solid #dbdbdb",
    borderTop: "none",
    "&:last-of-type": {
        marginBottom: "8px",
    },
});

const ManagementHeading = styled("h3")({
    margin: "7px 0",
});

const MoveItButton = styled(Button)({
    margin: "0 0 10px",
    padding: 0,
    "&:hover": {
        background: "transparent",
    },
});

// New styled components for grid replacement
const FlexContainer = styled("div")({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
});

const FlexItem = styled("div")({
    display: "flex",
    alignItems: "center",
});

const PropertyManagementInformation = (props) => {
    const { autocompleteResults, data, disableInputs, errors, handleDeleteArrayElement, handleInput, searchKMPerson } =
        props;

    const handleUpdatePersonnelRole = (currentRole, currentPersonnel, newRole) => {
        const currentRoles = Object.keys(currentPersonnel);
        const updatedPersonnel = {};
        currentRoles.forEach((role) => {
            if (role === currentRole) {
                updatedPersonnel[newRole] = currentPersonnel[currentRole];
                updatedPersonnel[newRole]["role"] = newRole;
            } else {
                updatedPersonnel[role] = currentPersonnel[role];
            }
        });

        handleInput("personnel", updatedPersonnel);
    };

    const handleNewPersonnel = () => {
        const propertyManagementKey = get(data, "propertyManagementKey");
        handleInput("personnel.Select", {});
        handleInput("personnel.Select.propertyManagementKey", propertyManagementKey);
        handleInput("personnel.Select.role", "Select");
    };

    const isAddPersonnelValid = () => {
        if (get(data, "personnel")) {
            const usedPersonnelRoles = new Set(Object.keys(get(data, "personnel")));
            if (usedPersonnelRoles.size === propertyManagerTypes.length || usedPersonnelRoles.has("Select")) {
                return true;
            }
        }
        return false;
    };

    const getOrderedPersonnel = () => {
        const orderedPersonnel = [];
        if (!("personnel" in data)) return orderedPersonnel;

        const personnelMap = data["personnel"];

        if ("Market Leader" in personnelMap) orderedPersonnel.push(personnelMap["Market Leader"]);
        if ("Senior Property Manager" in personnelMap) orderedPersonnel.push(personnelMap["Senior Property Manager"]);
        if ("Property Manager" in personnelMap) orderedPersonnel.push(personnelMap["Property Manager"]);
        if ("Property Admin" in personnelMap) orderedPersonnel.push(personnelMap["Property Admin"]);
        if ("Accounting" in personnelMap) orderedPersonnel.push(personnelMap["Accounting"]);
        if ("Associate Property Manager" in personnelMap)
            orderedPersonnel.push(personnelMap["Associate Property Manager"]);
        if ("Operations Manager" in personnelMap) orderedPersonnel.push(personnelMap["Operations Manager"]);
        if ("Other" in personnelMap) orderedPersonnel.push(personnelMap["Other"]);
        if ("Select" in personnelMap) orderedPersonnel.push(personnelMap["Select"]);

        return orderedPersonnel;
    };

    const disabledManagementRoles = ["Market Leader", "Property Admin", "Accounting"];

    const getPersonnelVal = (personnel) => {
        return personnel.person
            ? get(personnel, `person.firstName`, "") + " " + get(personnel, `person.lastName`, "")
            : "";
    };

    return (
        <ManagementInfoContainer>
            <ManagementInfoContainerHeader>
                <FlexContainer>
                    <div>
                        <ManagementHeading>Management Information</ManagementHeading>
                    </div>
                    <div>
                        <FlexItem>
                            <div>
                                <DefTerm>Commencement Date:</DefTerm>
                            </div>
                            <div>
                                <BSInput
                                    id="commencementDate"
                                    value={data.commencementDate}
                                    placeholder="Enter date"
                                    errorPlaceholder="hideIt"
                                    onChange={(e) => handleInput(e.target.id, e.target.value)}
                                    disabled={disableInputs}
                                    noFullWidth={true}
                                    type="date"
                                    noHelperText={true}
                                />
                            </div>
                            <div>
                                <BSSelect
                                    id="commencementDateType"
                                    value={data.commencementDateType}
                                    placeholder="Effective Date"
                                    onChange={(e) => handleInput(e.target.id, e.target.value)}
                                    combinedField="labelLessShiftLeft"
                                    disabled={disableInputs}
                                    noHelperText={true}
                                    options={[
                                        {
                                            value: "",
                                            label: "Select",
                                            disabled: true,
                                        },
                                        ...commencementDateTypes,
                                    ]}
                                />
                            </div>
                        </FlexItem>
                    </div>
                </FlexContainer>
            </ManagementInfoContainerHeader>
            {getOrderedPersonnel().map((element, i) => (
                <ManagementInfoContainerRow
                    key={i}
                    onDelete={(index) => handleDeleteArrayElement("personnel", index)}
                    {...props} // This needs to be refactored
                >
                    <FlexContainer>
                        <div style={{ width: "100%" }}>
                            <FlexItem>
                                <div>
                                    <Autocomplete
                                        id="name"
                                        error={
                                            get(errors, `personnel.${i}.person`, null) !== null
                                                ? get(errors, `personnel.${i}.person`, null)
                                                : get(errors, `personnel.${i}.role`, null) !== null
                                                ? " "
                                                : null
                                        }
                                        extraFormControlStyles={{ marginBottom: "-1px" }}
                                        initialInputValue={getPersonnelVal(element)}
                                        itemToString={(i) => (i == null ? "" : i.firstName + " " + i.lastName)}
                                        onSelect={(person) => handleInput(`personnel.${element.role}.person`, person)}
                                        options={{
                                            key: "person",
                                            display: (suggestion) => {
                                                return suggestion.firstName + " " + suggestion.lastName;
                                            },
                                        }}
                                        placeholder="Enter name"
                                        searchFunction={(input) => searchKMPerson(input)}
                                        startAdornment={<Search />}
                                        suggestions={autocompleteResults}
                                        disabled={disableInputs}
                                    />
                                </div>
                                <div>
                                    <BSSelect
                                        id="role"
                                        error={
                                            get(errors, `personnel.${i}.role`)
                                                ? get(errors, `personnel.${i}.role`)
                                                : get(errors, `personnel.${i}.person`)
                                                ? " "
                                                : null
                                        }
                                        value={element.role === "Select" ? "" : element.role}
                                        placeholder="Select"
                                        onChange={(e) =>
                                            handleUpdatePersonnelRole(
                                                element.role,
                                                get(data, "personnel"),
                                                e.target.value
                                            )
                                        }
                                        combinedField="labelLessShiftLeft"
                                        options={[
                                            {
                                                value: "",
                                                label: "Select",
                                                disabled: true,
                                            },
                                            ...map(propertyManagerTypes, function (p) {
                                                return {
                                                    ...p,
                                                    disabled: includes(
                                                        [...Object.keys(get(data, "personnel", {}))],
                                                        p.value
                                                    ),
                                                };
                                            }),
                                        ]}
                                        disabled={includes(disabledManagementRoles, element.role) || disableInputs}
                                    />
                                </div>
                            </FlexItem>
                        </div>
                    </FlexContainer>
                </ManagementInfoContainerRow>
            ))}
            {disableInputs !== true && (
                <MoveItButton color="primary" onClick={() => handleNewPersonnel()} disabled={isAddPersonnelValid()}>
                    <Add fill={isAddPersonnelValid() ? "#DCDCDC" : "#4b94c1"} /> Add Personnel
                </MoveItButton>
            )}
        </ManagementInfoContainer>
    );
};

export default PropertyManagementInformation;
